import { Mic, Send, Volume2, X } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ApiClientError, api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { listenOnce, speak, speechRecognitionSupported, stopSpeaking } from "@/lib/voice";

interface Turn {
  id: number;
  question: string;
  answer: string;
  costUsd: number;
  model: string;
}

/**
 * Floating assistant button, present on every authenticated page (Layout
 * renders it, gated on the same "marketplaces" scope the backend route
 * requires — see server/routes/assistant.ts). Isaac's ask: a Jarvis/Siri-style
 * button that gives a spoken rundown of sales and can answer follow-ups.
 *
 * Deliberately a hand-rolled fixed-position widget rather than the Dialog
 * component — this is meant to sit alongside whatever page is already open
 * (Sales, Inventory, anywhere), not take it over the way a modal would.
 */
export function VoiceAssistant() {
  const { t } = useTranslation("assistant");
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [pending, setPending] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const canListen = useRef(speechRecognitionSupported()).current;
  const nextTurnId = useRef(0);

  async function ask(question: string) {
    const trimmed = question.trim();
    if (!trimmed || pending) return;
    setError(null);
    setPending(true);
    setInputValue("");
    try {
      const { answer, model, costUsd } = await api<{
        answer: string;
        model: string;
        costUsd: number;
      }>("/api/v1/assistant/ask", {
        method: "POST",
        body: JSON.stringify({ question: trimmed }),
      });
      const id = nextTurnId.current++;
      setTurns((prev) => [...prev, { id, question: trimmed, answer, model, costUsd }]);
      setSpeaking(true);
      await speak(answer);
      setSpeaking(false);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 503) {
        setError(t("notConfigured"));
      } else {
        setError(t("askFailed"));
      }
    } finally {
      setPending(false);
    }
  }

  async function handleMic() {
    if (!canListen) return;
    setError(null);
    setListening(true);
    try {
      const transcript = await listenOnce();
      await ask(transcript);
    } catch (err) {
      // Surface the ACTUAL browser error rather than one generic message for
      // every failure mode — "no mic found" and "permission blocked" need
      // completely different fixes from the user, and from us debugging it.
      const reason = err instanceof Error ? err.message : "unknown";
      console.error("voice input failed:", reason);
      if (reason === "not-allowed" || reason === "permission-denied") {
        setError(t("micBlocked"));
      } else if (reason === "audio-capture") {
        setError(t("micNotFound"));
      } else if (reason === "network") {
        setError(t("micNetworkError"));
      } else {
        setError(t("didNotCatchThat"));
      }
    } finally {
      setListening(false);
    }
  }

  function handleClose() {
    stopSpeaking();
    setSpeaking(false);
    setOpen(false);
  }

  /** Real per-turn costs land well under a cent (stack §14c's own reference
   * point: "tenths of a cent/turn") — showing "$0.00" would read as free and
   * defeat the point of surfacing it at all, so sub-cent answers are shown
   * in ¢ instead. */
  function formatCost(usd: number): string {
    if (usd <= 0) return t("costFree");
    if (usd < 0.01) return `${(usd * 100).toFixed(3)}¢`;
    return `$${usd.toFixed(2)}`;
  }

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      {open && (
        <div
          role="dialog"
          aria-label={t("panelTitle")}
          className="flex w-[min(24rem,calc(100vw-2.5rem))] flex-col rounded-xl border bg-card text-card-foreground shadow-lg"
        >
          <div className="flex items-center justify-between border-b px-4 py-3">
            <span className="font-medium">{t("panelTitle")}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              aria-label={t("close")}
              onClick={handleClose}
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>

          <div
            className="flex max-h-80 flex-col gap-3 overflow-y-auto px-4 py-3"
            aria-live="polite"
          >
            {turns.length === 0 && !pending && (
              <p className="text-sm text-muted-foreground">{t("prompt")}</p>
            )}
            {turns.map((turn) => (
              <div key={turn.id} className="flex flex-col gap-1">
                <p className="text-sm font-medium">{turn.question}</p>
                <p className="text-sm text-muted-foreground">{turn.answer}</p>
                <p className="tabular-nums text-xs text-muted-foreground/70">
                  {t("costLabel", { cost: formatCost(turn.costUsd), model: turn.model })}
                </p>
              </div>
            ))}
            {listening && (
              <p className="flex items-center gap-1.5 text-sm font-medium text-primary">
                <Mic className="h-3.5 w-3.5 animate-pulse" aria-hidden />
                {t("listening")}
              </p>
            )}
            {pending && <p className="text-sm text-muted-foreground">{t("thinking")}</p>}
            {speaking && (
              <p className="flex items-center gap-1.5 text-xs text-primary">
                <Volume2 className="h-3.5 w-3.5" aria-hidden />
                {t("speaking")}
              </p>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <form
            className="flex items-center gap-2 border-t p-3"
            onSubmit={(e) => {
              e.preventDefault();
              void ask(inputValue);
            }}
          >
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t("inputPlaceholder")}
              className="tap h-10 flex-1 rounded-md border bg-background px-3 text-sm"
              disabled={pending}
            />
            {canListen && (
              <Button
                type="button"
                variant={listening ? "default" : "outline"}
                size="sm"
                className="tap h-10 w-10 shrink-0 p-0"
                aria-label={t("speakQuestion")}
                onClick={() => void handleMic()}
                disabled={pending || listening}
              >
                <Mic className="h-4 w-4" aria-hidden />
              </Button>
            )}
            <Button
              type="submit"
              size="sm"
              className="tap h-10 w-10 shrink-0 p-0"
              aria-label={t("send")}
              disabled={pending || !inputValue.trim()}
            >
              <Send className="h-4 w-4" aria-hidden />
            </Button>
          </form>
        </div>
      )}

      <Button
        size="lg"
        className={cn("tap h-14 w-14 rounded-full p-0 shadow-lg", open && "hidden")}
        aria-label={t("openAssistant")}
        onClick={() => setOpen(true)}
      >
        <Mic className="h-6 w-6" aria-hidden />
      </Button>
    </div>
  );
}
